// Koyfin deep links. Koyfin's security pages are keyed by an internal id (a "KID" such as eq-owh25d), not by the
// ticker: /snapshot/s/AAPL renders an empty page. So the id is looked up first through the app's own symbol
// conversion endpoint, which needs no key and answers CORS with "*" (verified 2026-09-24; design.md "Koyfin 外链").
// Koyfin's identifiers are TICKER:COUNTRY with dots dropped (BRK.B → BRKB:US, REI.UN → REIUN:CA); FundSERV codes
// (FID5982:CA) and crypto pairs (BTCUSD:CUR) resolve as well.
import type { Asset } from '@/types';
import { marketFetch } from './errors';

const CONVERT_URL = 'https://app.koyfin.com/api/v3/tickers/convert/symbols-to-ids';
const STORAGE_KEY = 'assetlist.koyfin.v1';

export interface KoyfinRow {
  KID?: string;
  identifier?: string;
  category?: string;
  name?: string;
}

/** How Koyfin spells this holding: the symbol to send and the identifier to pick from the answer. Cash has no page. */
export function koyfinSymbol(asset: Pick<Asset, 'assetClass' | 'symbol' | 'market'>): { symbol: string; identifier: string } | null {
  const raw = asset.symbol.toUpperCase();
  if (asset.assetClass === 'cash') return null;
  if (asset.assetClass === 'crypto') return { symbol: `${raw}USD`, identifier: `${raw}USD:CUR` };
  const symbol = raw.replace(/\./g, '');
  const country = asset.assetClass === 'fund' ? 'CA' : asset.market === 'CA' ? 'CA' : 'US';
  return { symbol, identifier: `${symbol}:${country}` };
}

/** Pure; exported for tests. The row for our exact identifier, else the first one listed in the same country. */
export function pickKoyfinRow(rows: KoyfinRow[], identifier: string): KoyfinRow | null {
  const exact = rows.find((r) => r.identifier?.toUpperCase() === identifier);
  if (exact?.KID) return exact;
  const country = identifier.split(':')[1];
  const same = rows.find((r) => r.KID && r.identifier?.toUpperCase().endsWith(`:${country}`));
  return same ?? null;
}

export function koyfinPageUrl(kid: string): string {
  return `https://app.koyfin.com/snapshot/s/${encodeURIComponent(kid)}`;
}

let cache: Record<string, string> | null = null; // identifier → KID; ids are stable, so this never expires

function loadCache(): Record<string, string> {
  if (cache) return cache;
  try {
    cache = JSON.parse((typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || '{}') as Record<string, string>;
  } catch {
    cache = {};
  }
  return cache;
}

function remember(identifier: string, kid: string) {
  const c = loadCache();
  c[identifier] = kid;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    // storage unavailable: the in-memory copy still serves this session
  }
}

/** The Koyfin page for a holding, or null when Koyfin does not list it. Throws only on network failure. */
export async function koyfinUrl(asset: Pick<Asset, 'assetClass' | 'symbol' | 'market'>, signal?: AbortSignal): Promise<string | null> {
  const want = koyfinSymbol(asset);
  if (!want) return null;
  const cached = loadCache()[want.identifier];
  if (cached) return koyfinPageUrl(cached);
  const res = await marketFetch(CONVERT_URL, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ symbols: [want.symbol], includeInactive: false }), signal }, 'Koyfin');
  const json = (await res.json()) as Record<string, KoyfinRow[] | undefined>;
  const row = pickKoyfinRow(json[want.symbol] ?? [], want.identifier);
  if (!row?.KID) return null;
  remember(want.identifier, row.KID);
  return koyfinPageUrl(row.KID);
}
